import * as Dialog from '@radix-ui/react-dialog';
import { Eye, EyeOff, Home, KeyRound, Plus, Star } from 'lucide-react';
import { useState, type FormEvent } from 'react';

import { Button } from '@/components/ui/button';
import { EmptyState } from '@/components/ui/empty-state';
import { Field, FormError, Label } from '@/components/ui/field';
import { RequiredFieldsNote } from '@/components/ui/form-section';
import { StatusBadge } from '@/components/ui/status-badge';
import { usePropertyAction, useSaveProperty } from '@/hooks/clients';
import { usePropertyCategories } from '@/hooks/property-categories';
import { ApiError } from '@/lib/api';
import { useToast } from '@/lib/toast-context';
import { cn } from '@/lib/utils';
import type { ClientDetail, ClientPropertyView } from '@shared/schemas/client';

/*
 * The Properties tab. ROADMAP.md §3.6.
 *
 * **This is where `access_notes` lives**, and it is the most sensitive field in
 * the application so far: lockbox codes, gate codes, alarm codes. It is
 * therefore hidden behind a deliberate press rather than printed on the page —
 * not as access control, which is server-side, but because a manager with this
 * screen open in an office should not be broadcasting somebody's front-door
 * code to whoever walks past.
 */

export function ClientPropertiesTab({ detail }: { detail: ClientDetail }) {
  const [editing, setEditing] = useState<ClientPropertyView | null>(null);
  const [adding, setAdding] = useState(false);

  return (
    <div className="space-y-3">
      <div className="flex justify-end">
        <Button
          onClick={() => {
            setAdding(true);
          }}
        >
          <Plus aria-hidden="true" />
          Add property
        </Button>
      </div>

      {detail.properties.length === 0 ? (
        <div className="rounded-lg border border-ink-300 bg-white shadow-card">
          <EmptyState
            icon={Home}
            title="No properties yet."
            hint="A property is an address a cleaner is sent to. Without one there is nowhere to schedule a job."
            action={
              <Button
                onClick={() => {
                  setAdding(true);
                }}
              >
                <Plus aria-hidden="true" />
                Add property
              </Button>
            }
          />
        </div>
      ) : (
        <ul className="space-y-3">
          {detail.properties.map((property) => (
            <PropertyCard
              key={property.id}
              clientId={detail.id}
              property={property}
              onEdit={() => {
                setEditing(property);
              }}
            />
          ))}
        </ul>
      )}

      <PropertyFormDialog
        clientId={detail.id}
        open={adding}
        onOpenChange={setAdding}
        hasProperties={detail.properties.length > 0}
      />
      <PropertyFormDialog
        clientId={detail.id}
        open={editing !== null}
        onOpenChange={(open) => {
          if (!open) setEditing(null);
        }}
        hasProperties
        {...(editing ? { property: editing } : {})}
      />
    </div>
  );
}

function PropertyCard({
  clientId,
  property,
  onEdit,
}: {
  clientId: string;
  property: ClientPropertyView;
  onEdit: () => void;
}) {
  const toast = useToast();
  const action = usePropertyAction(clientId);
  const [codesVisible, setCodesVisible] = useState(false);

  function run(next: 'primary' | 'retire' | 'reactivate', message: string) {
    action.mutate(
      { propertyId: property.id, action: next },
      {
        onSuccess: () => {
          toast.success(message);
        },
        onError: (cause) => {
          toast.error(cause instanceof ApiError ? cause.message : 'That could not be saved.');
        },
      },
    );
  }

  return (
    <li
      className={cn(
        'rounded-lg border bg-white p-4 shadow-card',
        property.isPrimary ? 'border-honey-500' : 'border-ink-300',
        property.isActive ? '' : 'opacity-70',
      )}
    >
      <div className="flex flex-col gap-3 sm:flex-row sm:items-start sm:justify-between">
        <div className="min-w-0">
          <p className="flex flex-wrap items-center gap-2 text-h3 text-ink-900">
            {property.nickname ?? property.address}
            {property.isPrimary ? (
              <span className="inline-flex items-center gap-1 rounded-full bg-honey-50 px-2 py-0.5 text-meta font-medium text-bark-900">
                <Star aria-hidden="true" className="size-3 fill-honey-400 text-bark-900" />
                Primary
              </span>
            ) : null}
            {property.isActive ? null : <StatusBadge label="Retired" tone="cancelled" />}
          </p>
          <p className="mt-0.5 text-body text-ink-500">{property.fullAddress}</p>

          <p className="mt-2 text-meta text-ink-700">
            {/* Zero is `0` and "not recorded" is its own answer — P1. */}
            {describe(property)}
          </p>

          {property.categoryName ? (
            <p className="mt-1 text-meta text-ink-500">Checklist: {property.categoryName}</p>
          ) : (
            <p className="mt-1 text-meta text-ink-500">
              No checklist category — a job here starts with an empty task list.
            </p>
          )}

          {property.specialInstructions ? (
            <p className="mt-2 text-meta text-ink-700">{property.specialInstructions}</p>
          ) : null}
        </div>

        <div className="flex shrink-0 flex-wrap gap-2">
          {property.isActive && !property.isPrimary ? (
            <Button
              variant="outline"
              size="sm"
              disabled={action.isPending}
              onClick={() => {
                run('primary', `${property.fullAddress} is now the primary address.`);
              }}
            >
              Make primary
            </Button>
          ) : null}
          <Button variant="outline" size="sm" onClick={onEdit}>
            Edit
          </Button>
          {property.isActive ? (
            <Button
              variant="outline"
              size="sm"
              disabled={action.isPending}
              onClick={() => {
                run('retire', 'Property retired.');
              }}
            >
              Retire
            </Button>
          ) : (
            <Button
              variant="outline"
              size="sm"
              disabled={action.isPending}
              onClick={() => {
                run('reactivate', 'Property is active again.');
              }}
            >
              Bring back
            </Button>
          )}
        </div>
      </div>

      {property.accessNotes ? (
        <div className="mt-3 rounded-sm border border-ink-300 bg-ink-100/40 p-3">
          <div className="flex flex-wrap items-center justify-between gap-2">
            <p className="flex items-center gap-1.5 text-label text-ink-900">
              <KeyRound aria-hidden="true" className="size-4" />
              Access notes
            </p>
            {/*
              Hidden until asked for. Not access control — that is server-side —
              but a lockbox code printed on a screen in an office is a lockbox
              code read by whoever walks past it.
            */}
            <Button
              variant="ghost"
              size="sm"
              aria-expanded={codesVisible}
              onClick={() => {
                setCodesVisible((current) => !current);
              }}
            >
              {codesVisible ? <EyeOff aria-hidden="true" /> : <Eye aria-hidden="true" />}
              {codesVisible ? 'Hide' : 'Show'}
            </Button>
          </div>
          {codesVisible ? (
            <p className="mt-2 whitespace-pre-wrap text-body text-ink-900">
              {property.accessNotes}
            </p>
          ) : (
            <p className="mt-2 text-meta text-ink-500">
              Hidden. Codes for getting in — a cleaner sees these only for a job they are assigned
              to.
            </p>
          )}
        </div>
      ) : null}
    </li>
  );
}

/** "3 bed · 2.5 bath · 1,800 sq ft", leaving out what nobody recorded. */
function describe(property: ClientPropertyView): string {
  const parts: string[] = [];
  if (property.bedrooms !== null) parts.push(`${String(property.bedrooms)} bed`);
  if (property.bathrooms !== null) parts.push(`${String(property.bathrooms)} bath`);
  if (property.squareFeet !== null) parts.push(`${property.squareFeet.toLocaleString()} sq ft`);

  return parts.length > 0 ? parts.join(' · ') : 'Size not recorded';
}

function PropertyFormDialog({
  clientId,
  open,
  onOpenChange,
  property,
  hasProperties,
}: {
  clientId: string;
  open: boolean;
  onOpenChange: (open: boolean) => void;
  property?: ClientPropertyView;
  hasProperties: boolean;
}) {
  return (
    <Dialog.Root open={open} onOpenChange={onOpenChange}>
      <Dialog.Portal>
        <Dialog.Overlay className="fixed inset-0 z-40 bg-black/40" />
        <Dialog.Content className="fixed left-1/2 top-1/2 z-50 max-h-[calc(100vh-2rem)] w-[calc(100vw-2rem)] max-w-lg -translate-x-1/2 -translate-y-1/2 overflow-y-auto rounded-lg border border-ink-300 bg-white p-6 shadow-pop">
          <PropertyForm
            clientId={clientId}
            property={property}
            hasProperties={hasProperties}
            onDone={() => {
              onOpenChange(false);
            }}
          />
        </Dialog.Content>
      </Dialog.Portal>
    </Dialog.Root>
  );
}

function PropertyForm({
  clientId,
  property,
  hasProperties,
  onDone,
}: {
  clientId: string;
  property?: ClientPropertyView;
  hasProperties: boolean;
  onDone: () => void;
}) {
  const toast = useToast();
  const isEdit = property !== undefined;
  const save = useSaveProperty(clientId);

  // Only the active categories, and only the first page — this is a dropdown of
  // property types, not a browsing surface.
  const categories = usePropertyCategories({ page: 1, search: '', activity: 'active' });

  const [nickname, setNickname] = useState(property?.nickname ?? '');
  const [address, setAddress] = useState(property?.address ?? '');
  const [city, setCity] = useState(property?.city ?? 'Cedar City');
  const [state, setState] = useState(property?.state ?? 'UT');
  const [zip, setZip] = useState(property?.zip ?? '');
  const [bedrooms, setBedrooms] = useState(
    property?.bedrooms === null || property?.bedrooms === undefined
      ? ''
      : String(property.bedrooms),
  );
  const [bathrooms, setBathrooms] = useState(
    property?.bathrooms === null || property?.bathrooms === undefined
      ? ''
      : String(property.bathrooms),
  );
  const [squareFeet, setSquareFeet] = useState(
    property?.squareFeet === null || property?.squareFeet === undefined
      ? ''
      : String(property.squareFeet),
  );
  const [categoryId, setCategoryId] = useState(property?.categoryId ?? '');
  const [specialInstructions, setSpecialInstructions] = useState(
    property?.specialInstructions ?? '',
  );
  const [accessNotes, setAccessNotes] = useState(property?.accessNotes ?? '');
  const [isPrimary, setIsPrimary] = useState(property?.isPrimary ?? false);

  const error = save.error instanceof ApiError ? save.error : null;
  const fieldErrors = error?.fieldErrors ?? {};

  function submit(event: FormEvent) {
    event.preventDefault();

    save.mutate(
      {
        propertyId: property?.id ?? null,
        input: {
          nickname,
          address,
          city,
          state,
          zip,
          bedrooms: bedrooms === '' ? null : Number(bedrooms),
          bathrooms: bathrooms === '' ? null : Number(bathrooms),
          squareFeet: squareFeet === '' ? null : Number(squareFeet),
          categoryId: categoryId === '' ? null : categoryId,
          specialInstructions,
          accessNotes,
          isPrimary,
        },
      },
      {
        onSuccess: (saved) => {
          toast.success(isEdit ? 'Property saved.' : `Added ${saved.fullAddress}.`);
          onDone();
        },
      },
    );
  }

  return (
    <>
      <Dialog.Title className="text-h2 text-ink-900">
        {isEdit ? 'Edit property' : 'Add a property'}
      </Dialog.Title>
      <Dialog.Description className="mt-1 text-body text-ink-500">
        This is the address a cleaner is sent to.
      </Dialog.Description>

      <form onSubmit={submit} className="mt-5 space-y-4">
        <RequiredFieldsNote />

        {error && Object.keys(fieldErrors).length === 0 ? (
          <FormError>{error.message}</FormError>
        ) : null}

        <Field
          label="Nickname"
          name="nickname"
          value={nickname}
          onChange={(event) => {
            setNickname(event.target.value);
          }}
          placeholder="Cedar Ave rental"
          hint="What the client calls it. Makes a request form readable when they have several."
        />

        <Field
          label="Street address"
          name="address"
          required
          value={address}
          error={fieldErrors.address}
          onChange={(event) => {
            setAddress(event.target.value);
          }}
        />

        <div className="grid gap-4 sm:grid-cols-3">
          <Field
            label="City"
            name="city"
            required
            value={city}
            error={fieldErrors.city}
            onChange={(event) => {
              setCity(event.target.value);
            }}
          />
          <Field
            label="State"
            name="state"
            required
            value={state}
            error={fieldErrors.state}
            onChange={(event) => {
              setState(event.target.value);
            }}
          />
          <Field
            label="ZIP"
            name="zip"
            required
            value={zip}
            error={fieldErrors.zip}
            onChange={(event) => {
              setZip(event.target.value);
            }}
          />
        </div>

        <div className="grid gap-4 sm:grid-cols-3">
          <Field
            label="Bedrooms"
            name="bedrooms"
            type="number"
            min={0}
            value={bedrooms}
            error={fieldErrors.bedrooms}
            onChange={(event) => {
              setBedrooms(event.target.value);
            }}
          />
          <Field
            label="Bathrooms"
            name="bathrooms"
            type="number"
            min={0}
            step={0.5}
            value={bathrooms}
            error={fieldErrors.bathrooms}
            onChange={(event) => {
              setBathrooms(event.target.value);
            }}
            hint="Halves allowed."
          />
          <Field
            label="Square feet"
            name="squareFeet"
            type="number"
            min={0}
            value={squareFeet}
            error={fieldErrors.squareFeet}
            onChange={(event) => {
              setSquareFeet(event.target.value);
            }}
          />
        </div>

        <div className="space-y-1.5">
          <Label htmlFor="property-category">Checklist category</Label>
          <select
            id="property-category"
            name="categoryId"
            value={categoryId}
            onChange={(event) => {
              setCategoryId(event.target.value);
            }}
            className="min-h-control w-full rounded-sm border border-ink-300 bg-white px-3 text-body"
          >
            <option value="">Not set</option>
            {(categories.data?.data ?? []).map((entry) => (
              <option key={entry.id} value={entry.id}>
                {entry.name}
              </option>
            ))}
          </select>
          <p className="text-meta text-ink-500">
            Decides the task list a job at this property starts with.
          </p>
        </div>

        <div className="space-y-1.5">
          <Label htmlFor="property-instructions">Special instructions</Label>
          <textarea
            id="property-instructions"
            name="specialInstructions"
            rows={2}
            value={specialInstructions}
            onChange={(event) => {
              setSpecialInstructions(event.target.value);
            }}
            className="w-full rounded-sm border border-ink-300 bg-white px-3 py-2 text-body"
            placeholder="Park on the street, not the driveway."
          />
        </div>

        <div className="space-y-1.5 rounded-sm border border-ink-300 bg-honey-50 p-3">
          <Label htmlFor="property-access">Access notes</Label>
          <textarea
            id="property-access"
            name="accessNotes"
            rows={3}
            value={accessNotes}
            onChange={(event) => {
              setAccessNotes(event.target.value);
            }}
            className="w-full rounded-sm border border-ink-300 bg-white px-3 py-2 text-body"
            placeholder="Lockbox 4821 on the gas meter. Dog is friendly, keep him inside."
          />
          <p className="text-meta text-bark-900">
            Codes for getting in. Kept hidden on this page until somebody asks for it, and a cleaner
            only ever sees it for a job they are assigned to.
          </p>
        </div>

        {hasProperties ? (
          <label className="flex min-h-touch items-center gap-2.5">
            <input
              type="checkbox"
              name="isPrimary"
              checked={isPrimary}
              onChange={(event) => {
                setIsPrimary(event.target.checked);
              }}
              className="size-4 shrink-0 accent-honey-500"
            />
            <span className="text-body text-ink-900">Make this the primary address</span>
          </label>
        ) : (
          <p className="text-meta text-ink-500">
            The first property is the primary one automatically.
          </p>
        )}

        <div className="flex flex-col-reverse gap-2 pt-1 sm:flex-row sm:justify-end">
          <Dialog.Close asChild>
            <Button variant="outline" disabled={save.isPending}>
              Cancel
            </Button>
          </Dialog.Close>
          <Button type="submit" disabled={save.isPending}>
            {save.isPending ? 'Saving…' : isEdit ? 'Save property' : 'Add property'}
          </Button>
        </div>
      </form>
    </>
  );
}
